import { createRoot as createClientRoot } from "react-dom/client";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ViteReactSSG } from "vite-react-ssg";
import { routes } from "@/App";
import { recoverChunkLoadError } from "@/lib/chunkRecovery";
import { loadLanguage } from "@/lib/translations";
import { getLanguageFromPath } from "@/lib/i18n";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";
import "@/index.css";
import "@/styles/field.css";

// vite-react-ssg can reject its initial lazy import before mounting Router.
// Later navigation failures are caught by RouteErrorBoundary instead.
if (typeof window !== "undefined") {
  window.addEventListener("error", (event) => {
    recoverChunkLoadError(event.error ?? event.message);
  });

  window.addEventListener("unhandledrejection", (event) => {
    recoverChunkLoadError(event.reason);
  });
}

/**
 * SSG entry point. `vite-react-ssg` calls this at build time to prerender
 * each included path, and again at runtime to hydrate the prerendered
 * HTML (or render from scratch on a SPA-fallback page).
 *
 * Providers and layout live in `<Layout />` (the `element` on the root
 * route) so every route - prerendered or SPA-only - shares them.
 * `HelmetProvider` is injected automatically by `vite-react-ssg` so we
 * don't wrap one here; `react-helmet-async` meta set via `<SEO>` is
 * captured into the static HTML head during prerender.
 */
const clientShell =
  typeof document !== "undefined" &&
  document.querySelector(
    'meta[name="fluxabee-render-mode"][content="client"]',
  ) !== null;

// The SSG entry is still used server-side and for prerendered public pages.
// Utility routes and real 404s receive an empty shell and mount with CSR.
const preloadEntryLanguage = async (pathname: string, client: boolean) => {
  const requests = [loadLanguage(getLanguageFromPath(pathname) ?? "en")];
  if (client && /^\/account(?:\/|$)/.test(pathname)) {
    try {
      const saved = window.localStorage.getItem("fluxabee-lang");
      if (saved === "en" || saved === "pl" || saved === "de") requests.push(loadLanguage(saved));
    } catch { /* Account pages retain the URL fallback when storage is blocked. */ }
  }
  await Promise.all(requests);
};

const handleBootstrapError = (error: unknown): never => {
  if (typeof window !== "undefined" && !recoverChunkLoadError(error)) {
    const container = document.getElementById("root");
    if (container) {
      const router = createBrowserRouter([{
        path: "*",
        loader: () => { throw error; },
        errorElement: <RouteErrorBoundary />,
      }], { future: { v7_relativeSplatPath: true } });
      createClientRoot(container).render(
        <RouterProvider router={router} future={{ v7_startTransition: true }} />,
      );
    }
  }
  // Stop the failed bootstrap; the existing global handler shares the reload guard.
  throw error;
};

export const createRoot = clientShell
  ? undefined
  : ViteReactSSG({ routes }, async ({ isClient, routePath }) => {
    const pathname = isClient ? window.location.pathname : routePath ?? "/";
    await preloadEntryLanguage(pathname, isClient).catch(handleBootstrapError);
  });

if (clientShell) {
  void (async () => {
    await preloadEntryLanguage(window.location.pathname, true);
    const container = document.getElementById("root");
    if (container) {
      const router = createBrowserRouter(routes, {
        future: { v7_relativeSplatPath: true },
      });
      createClientRoot(container).render(
        <HelmetProvider>
          <RouterProvider router={router} future={{ v7_startTransition: true }} />
        </HelmetProvider>,
      );
    }
  })().catch(handleBootstrapError);
}
